import { X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  OfferCard,
  StandingOfferRow,
} from "@sill/shared/components/marketplace/offer-card";

import type {
  OfferBuyerTier,
  OfferForBuyer,
  StandingMatch,
} from "../../lib/marketplace-offers-api";
import type { ProBrowseCard } from "../../lib/marketplace-discovery-api";
import {
  COMPETITION_MILESTONE_AGENT_COUNT,
  PASSPORT_HREF,
  PUBLISH_HREF,
  toCardSavings,
} from "../../lib/offers-display";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { CelebrationBanner } from "./CelebrationBanner";
import { TargetConfirmLine } from "./TargetConfirmLine";
import { YourAgentCard } from "./YourAgentCard";

/** Pending dismiss awaiting confirmation. `label` is the agent display name
 *  for the dialog copy; `kind` routes the confirm to the right handler. */
type DismissTarget =
  | { kind: "offer"; id: string; label: string }
  | { kind: "standing"; id: string; label: string };

/**
 * Presentational body of the Your Offers surface (task:m5-your-offers-page,
 * task:m5-empty-states, task:m5-celebration): "Made for you" above
 * "Available to you", the once-per-buyer celebration banner, and both empty
 * states. Data + handlers only — no `useAuth`/`usePostHog`/fetch here, so
 * the same component powers both the real Clerk/API-backed `OffersPage` and
 * the DEV-only, unauthed `OffersPreviewPage` fixture render. No accept CTA
 * anywhere (M5 non-goal) — `OfferCard` is display-only by construction.
 */
export interface OffersInboxProps {
  emptyState: "unpublished" | "quiet" | "populated";
  madeForYou: OfferForBuyer[];
  availableToYou: StandingMatch[];
  onDismissOffer: (offerId: string) => void;
  onDismissStanding: (proUserId: string) => void;
  onOpenOffer: (offerId: string) => void;
  /** The pro's PROFILE page — used for the Your-agent card. */
  standingHref: (proUserId: string) => string;
  /**
   * The standalone page for ONE standing offer at ONE tier — where an
   * "Available to you" row lands. Defaults to `${standingHref}/offers/${tier}`,
   * which is the shape of both the real (`/market/agents/:id/offers/:ref`)
   * and the DEV preview route. A row used to land on the profile and make
   * the buyer find the tier again; "View offer" should open the offer.
   */
  standingOfferHref?: (proUserId: string, tier: OfferBuyerTier) => string;
  yourAgent?: ProBrowseCard | null;
  yourAgentHref?: (proUserId: string) => string;
  onShareCompetition?: () => void;
  onShareFirstOffer?: () => void;
  /**
   * Server fact (plan 454 M5): `inbox.area_launched`. The not-live state keys
   * on THIS, never on an empty inbox or on waitlist membership — a launched
   * market with zero offers is the quiet state, not "isn't live".
   */
  areaLaunched: boolean;
  /**
   * Server fact: `inbox.area_label` (gazetteer "City, ST" for the target ZIP).
   * Empty alongside `areaLaunched === false` means the target has no resolved
   * ZIP (legacy row) — the confirm line renders instead of a geography claim.
   */
  areaLabel: string;
}

export function OffersInbox({
  emptyState,
  madeForYou,
  availableToYou: allAvailableToYou,
  onDismissOffer,
  onDismissStanding,
  onOpenOffer,
  standingHref,
  standingOfferHref = (proUserId, tier) =>
    `${standingHref(proUserId)}/offers/${tier}`,
  yourAgent,
  yourAgentHref = standingHref,
  onShareCompetition,
  onShareFirstOffer,
  areaLaunched,
  areaLabel,
}: OffersInboxProps) {
  // The buyer's own agent already has the "Your agent" card above; their
  // standing match would repeat the same agent and offer below it.
  const yourAgentId = yourAgent?.identity.pro_user_id;
  const availableToYou = yourAgentId
    ? allAvailableToYou.filter((match) => match.pro_user_id !== yourAgentId)
    : allAvailableToYou;
  const targetUnresolved = !areaLaunched && areaLabel === "";
  const notLive = !areaLaunched && areaLabel !== "";
  // Dismiss is not easily reversible (the offer/pro leaves this inbox), so it
  // routes through a confirmation dialog instead of firing on the first click.
  const [dismissTarget, setDismissTarget] = useState<DismissTarget | null>(
    null,
  );
  const [competitionDismissed, setCompetitionDismissed] = useState(false);

  const confirmDismiss = () => {
    if (!dismissTarget) return;
    if (dismissTarget.kind === "offer") onDismissOffer(dismissTarget.id);
    else onDismissStanding(dismissTarget.id);
    setDismissTarget(null);
  };
  const competingAgents = Array.from(
    new Map(
      [
        ...madeForYou.map((offer) => ({
          id: offer.pro_user_id,
          name: offer.pro_display_name,
          photoUrl: offer.pro_photo_url,
        })),
        ...availableToYou.map((match) => ({
          id: match.pro_user_id,
          name: match.pro_display_name,
          photoUrl: match.pro_photo_url,
        })),
      ].map((agent) => [agent.id, agent]),
    ).values(),
  );
  const showCompetition =
    competingAgents.length >= COMPETITION_MILESTONE_AGENT_COUNT &&
    !competitionDismissed;
  // The inbox is the authoritative delivered-offer read model. Do not infer
  // this from distinct agents: one agent can have more than one visible row.
  const hasExactlyOneOffer = madeForYou.length + availableToYou.length === 1;

  return (
    <>
      {showCompetition && (
        <CelebrationBanner
          agents={competingAgents}
          onDismiss={() => setCompetitionDismissed(true)}
          onShare={onShareCompetition}
        />
      )}

      {hasExactlyOneOffer && onShareFirstOffer ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4"
          data-testid="offers-first-offer-share-prompt"
        >
          <div>
            <p className="text-sm font-bold text-foreground">
              Your first offer is here
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Share the milestone and invite a friend to get agents competing
              too.
            </p>
          </div>
          <Button
            data-testid="offers-first-offer-share"
            onClick={onShareFirstOffer}
            size="sm"
            variant="outline"
          >
            Share milestone
          </Button>
        </div>
      ) : null}

      {yourAgent && (
        <section className="space-y-3" data-testid="offers-your-agent">
          <SectionLabel count={1} label="Your agent" />
          <YourAgentCard
            agent={yourAgent}
            href={yourAgentHref(yourAgent.identity.pro_user_id)}
          />
        </section>
      )}

      {targetUnresolved && emptyState !== "populated" && (
        <TargetConfirmLine testId="offers-target-confirm" />
      )}

      {/* Not-live is a fact about the target area, so it renders for an
          unpublished buyer too (Home's area_not_launched outranks the
          unpublished_* kinds; the surfaces must not disagree). */}
      {!targetUnresolved && !notLive && emptyState === "unpublished" && (
        <div
          className="marketplace-surface p-6 text-center"
          data-testid="offers-empty-unpublished"
        >
          <p className="text-base font-semibold text-foreground">
            Publish to let agents compete
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Publishing your passport puts you in front of agents with real
            incentive offers.
          </p>
          <Link
            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            to={PUBLISH_HREF}
          >
            Publish my passport
          </Link>
        </div>
      )}

      {!targetUnresolved &&
        (emptyState === "quiet" || (notLive && emptyState === "unpublished")) && (
        <div
          className="marketplace-surface p-6 text-center"
          data-testid="offers-empty-quiet"
          data-queued={notLive}
        >
          <p className="text-base font-semibold text-foreground">
            {notLive
              ? `Sill isn't live in ${areaLabel} yet`
              : "Your passport is live to Marketplace agents — no offers yet"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {notLive
              ? `There are no agents here to make offers yet. Offers open when we launch in ${areaLabel}.`
              : "Standing offers arrive as soon as an agent serving your area sets one for your tier."}
          </p>
          <Link
            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full border border-border bg-popover px-5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
            data-testid="offers-quiet-view-passport"
            to={PASSPORT_HREF}
          >
            View Passport
          </Link>
        </div>
      )}

      {emptyState === "populated" && (
        <>
          {madeForYou.length > 0 && (
            <section className="space-y-3" data-testid="offers-made-for-you">
              <SectionLabel count={madeForYou.length} label="Made for you" />
              <div className="space-y-3">
                {madeForYou.map((offer) => (
                  <MadeForYouRow
                    key={offer.offer_id}
                    offer={offer}
                    onDismiss={() =>
                      offer.offer_id &&
                      setDismissTarget({
                        kind: "offer",
                        id: offer.offer_id,
                        label: offer.pro_display_name ?? "this agent",
                      })
                    }
                    onOpen={() => offer.offer_id && onOpenOffer(offer.offer_id)}
                  />
                ))}
              </div>
            </section>
          )}

          {availableToYou.length > 0 && (
            <section
              className="space-y-3"
              data-testid="offers-available-to-you"
            >
              <SectionLabel
                count={availableToYou.length}
                label="Available to you · standing offers"
                quiet
              />
              <div className="grid gap-3 md:auto-rows-fr md:grid-cols-2">
                {availableToYou.map((match) => (
                  <AvailableToYouRow
                    key={match.pro_user_id}
                    href={standingOfferHref(
                      match.pro_user_id,
                      match.primary.buyer_tier,
                    )}
                    match={match}
                    onDismiss={() =>
                      setDismissTarget({
                        kind: "standing",
                        id: match.pro_user_id,
                        label: match.pro_display_name ?? "this agent",
                      })
                    }
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Dismiss confirmation — dismissing removes the offer/pro from this
          inbox (a standing pro can re-appear later; a tailored offer does not
          return), so confirm before firing. */}
      <Dialog
        onOpenChange={(open) => {
          if (!open) setDismissTarget(null);
        }}
        open={dismissTarget !== null}
      >
        <DialogContent
          description={
            dismissTarget?.kind === "standing" ? (
              <span data-testid="dismiss-confirm-body">
                Hide standing offers from {dismissTarget.label}? They&apos;ll
                stop showing here — you can still find them by browsing agents.
              </span>
            ) : (
              <span data-testid="dismiss-confirm-body">
                Dismiss this offer from {dismissTarget?.label}? It will be
                removed from Your Offers.
              </span>
            )
          }
          showClose
          title={
            dismissTarget?.kind === "standing"
              ? "Hide these offers?"
              : "Dismiss this offer?"
          }
        >
          <div className="mt-4 flex flex-col gap-2">
            <Button
              className="w-full"
              data-testid="dismiss-confirm-button"
              onClick={confirmDismiss}
              variant="destructive"
            >
              {dismissTarget?.kind === "standing"
                ? "Yes, hide"
                : "Yes, dismiss"}
            </Button>
            <DialogClose asChild>
              <Button className="w-full" variant="outline">
                Keep it
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Plan 435 M3 (mfh-offers-teaser). Exported as the ONE block-heading
 * treatment on Home — reused verbatim by `components/home/OffersTeaser`,
 * `AgentPreview`, `NextMove`, and `PrepareRow` (code-review fix, M3
 * iteration 2, finding 6: design calls for every block heading to share
 * one style — font size/weight/case/colour/letter-spacing — instead of
 * forking a second `.eyebrow-label` treatment with no divider rule).
 * `count` is optional: Offers/Agents have a natural number to show;
 * Next move/Prepare don't, and omit the trailing figure while keeping the
 * SAME divider rule.
 */
export function SectionLabel({
  count,
  label,
  quiet = false,
}: {
  count?: number;
  label: string;
  quiet?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-[10px] font-bold uppercase tracking-[0.13em] ${
          quiet ? "text-muted-foreground" : "text-foreground/60"
        }`}
      >
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
      {typeof count === "number" ? (
        <span className="tabular-nums text-xs text-muted-foreground">
          {count}
        </span>
      ) : null}
    </div>
  );
}

function MadeForYouRow({
  offer,
  onDismiss,
  onOpen,
}: {
  offer: OfferForBuyer;
  onDismiss: () => void;
  onOpen: () => void;
}) {
  return (
    <div data-testid="made-for-you-row">
      <OfferCard
        data={{
          commissionPct: offer.commission_pct,
          chrome: offer.own_agent ? "own-agent" : undefined,
          deliveryState: offer.delivery_state,
          estimatedSavings: toCardSavings(offer.estimated_savings),
          expiresAt: offer.expires_at,
          items: offer.items,
          note: offer.note,
          proBrokerage: offer.pro_brokerage,
          proDisplayName: offer.pro_display_name,
          proPhotoUrl: offer.pro_photo_url,
          actionLabel: "View offer",
        }}
        footerActions={
          <div className="flex items-center gap-4">
            <button
              aria-label={`Dismiss offer from ${offer.pro_display_name ?? "this agent"}`}
              className="min-h-10 text-sm font-semibold text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid="dismiss-offer"
              onClick={onDismiss}
              type="button"
            >
              Dismiss
            </button>
            <button
              className="inline-flex min-h-10 items-center gap-2 text-sm font-bold text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid="made-for-you-open"
              onClick={onOpen}
              type="button"
            >
              View offer
              <span aria-hidden>›</span>
            </button>
          </div>
        }
      />
    </div>
  );
}

function AvailableToYouRow({
  href,
  match,
  onDismiss,
}: {
  href: string;
  match: StandingMatch;
  onDismiss: () => void;
}) {
  // The × sits ON the card (top-right corner) but is a SIBLING of the link,
  // not a child — a button inside an anchor is invalid HTML. The row reserves
  // the corner via `topRightInset`; the button is overlaid absolutely.
  return (
    // `h-full` down the chain: the grid stretches each cell, and the wrapper
    // + link pass that height to the card's own `h-full`, so 2-up cards in a
    // row share one height with their footers aligned.
    <div className="relative h-full" data-testid="available-to-you-row">
      <Link
        className="group block h-full min-w-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid="available-to-you-open"
        to={href}
      >
        <StandingOfferRow
          data={{
            buyerTier: match.primary.buyer_tier,
            commissionPct: match.primary.commission_pct,
            deliveryState: match.primary.delivery_state,
            // The match-level field mirrors primary's; read either.
            estimatedSavings: toCardSavings(
              match.estimated_savings ?? match.primary.estimated_savings,
            ),
            items: match.primary.items,
            proBrokerage: match.pro_brokerage,
            proDisplayName: match.pro_display_name,
            proPhotoUrl: match.pro_photo_url,
            standing: true,
          }}
          topRightInset
        />
      </Link>
      <button
        aria-label={`Hide standing offers from ${match.pro_display_name ?? "this agent"}`}
        className="absolute right-1.5 top-1.5 flex size-10 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        data-testid="dismiss-standing"
        onClick={onDismiss}
        type="button"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
